import React, { useEffect, useState } from "react";
import {
  Button,
  TextField,
  MenuItem,
  Typography,
  Box,
  Grid,
  Tooltip,
  Card,
  CardContent,
  IconButton,
  Menu,
  List,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import DeleteIcon from "@mui/icons-material/Delete";
import { grey } from "@mui/material/colors";

const DEFAULT_PROMPTS = [
  { name: "client_name", label: "Client Name", type: "text", required: true, disable: false },
  { name: "client_address", label: "Client Address", type: "text", required: true, disable: false },
  { name: "client_gstin", label: "Client GSTIN", type: "text", required: true, disable: false },
  { name: "org_name", label: "Org Name", type: "text", required: true, disable: false },
  { name: "org_address", label: "Org Address", type: "text", required: true, disable: false },
  { name: "authority_name", label: "Authority Name", type: "text", required: true, disable: false },
  { name: "authority_title", label: "Authority Title", type: "text", required: true, disable: false },
  { name: "authority_place", label: "Authority Place", type: "text", required: true, disable: false },
  { name: "start_date", label: "Start Date", type: "date", required: true, disable: false },
  { name: "period_months", label: "Period Months", type: "number", required: true, disable: false },
  { name: "commission_pct", label: "Commission Pct", type: "number", required: true, disable: false },
  { name: "fee_basis", label: "Fee Basis", type: "text", required: true, disable: false },
  { name: "replacement_days", label: "Replacement Days", type: "number", required: true, disable: false },
  { name: "fee_basis_inclusion", label: "Fee Basis Inclusion", type: "text", required: true, disable: false }
];

function AgreementPrompt({ setCurrentMode, activeStep, setActiveStep, data, setData, agreementType, setAgreementType }) {
  const [formFields, setFormFields] = useState(DEFAULT_PROMPTS);
  const [menu, setMenu] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (data?.template_content) {
      const match = data.template_content.match(/<!--FIELDS_JSON:(.*)-->/);
      if (match) {
        try {
          const parsed = JSON.parse(match[1]);
          if (Array.isArray(parsed) && parsed.length > 0) {
            const mapped = parsed.map(f => ({ ...f, disable: false }));
            setFormFields(mapped);
          }
        } catch (e) {
          console.error("Failed to parse custom prompts JSON:", e);
        }
      }
    }
  }, [data]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenu(true);
  };

  const addField = () => {
    const newField = {
      name: `field${formFields.length + 1}`,
      label: "",
      type: "text",
      required: true,
      disable: false,
    };
    setFormFields([...formFields, newField]);
    setMenu(false);
  };

  const removeField = (index) => {
    const updatedFields = [...formFields];
    updatedFields.splice(index, 1);
    setFormFields(updatedFields);
  };

  const handleChange = (e, index) => {
    const updatedFields = [...formFields];
    updatedFields[index].label = e.target.value;
    updatedFields[index].name = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_");
    setFormFields(updatedFields);
  };

  const validate = () => {
    let temp = {};
    temp.agreementType = agreementType ? "" : "Agreement Type is required";
    formFields.forEach((field, index) => {
      if (!field.label.trim()) {
        temp[`label${index}`] = `Prompt ${index + 1} is required`;
      }
    });
    setErrors(temp);
    return Object.values(temp).every((x) => x === "");
  };

  const onSubmit = () => {
    if (validate()) {
      setData(prev => ({
        ...prev,
        agreement_type: agreementType,
        custom_fields: formFields
      }));
      setActiveStep(activeStep + 1);
    }
  };

  return (
    <Box sx={{ maxHeight: "85vh", overflowY: "auto", pb: 2 }}>
      <Box>
        <Typography sx={{ mb: 1, textAlign: "center", fontWeight: 600 }} variant="h6">
          Prompt Customization
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Grid container spacing={2} style={{ justifyContent: "center" }}>
            <Grid sx={{ width: "100%" }} item>
              <Card>
                <CardContent>
                  <TextField
                    label="Agreement Type / Title"
                    size="small"
                    variant="standard"
                    margin="normal"
                    fullWidth
                    value={agreementType}
                    disabled={!!data?.id}
                    required={true}
                    sx={{ mb: 4 }}
                    inputProps={{ style: { fontSize: 23 } }}
                    InputLabelProps={{ style: { fontSize: 20 } }}
                    onChange={(e) => setAgreementType(e.target.value)}
                    {...(errors.agreementType && {
                      error: true,
                      helperText: errors.agreementType,
                    })}
                  />
                  <Box
                    style={{
                      backgroundColor: grey[100],
                      borderRadius: "10px",
                    }}
                  >
                    <Grid container spacing={2} direction={"row"} p={2}>
                      {formFields.map((field, index) => (
                        <Grid item xs={12} key={index}>
                          <Box display="flex" alignItems="center" mb={1}>
                            <TextField
                              label="Prompt"
                              value={field.label}
                              size="small"
                              fullWidth
                              disabled={field.disable}
                              onChange={(e) => handleChange(e, index)}
                              {...(errors[`label${index}`] && {
                                error: true,
                                helperText: errors[`label${index}`],
                              })}
                            />
                            {!field.disable && (
                              <Tooltip title="Remove Field">
                                <IconButton
                                  onClick={() => removeField(index)}
                                  size="small"
                                  color="error"
                                  sx={{ ml: 1 }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                    <Box sx={{ width: "100%", p: 2 }}>
                      <Tooltip title="Add New Prompt">
                        <IconButton
                          onClick={handleMenu}
                          className="mpl-primary-btn"
                        >
                          <Add fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Menu
                        anchorEl={anchorEl}
                        open={menu}
                        onClose={() => setMenu(false)}
                        anchorOrigin={{
                          vertical: "center",
                          horizontal: "right",
                        }}
                        transformOrigin={{
                          vertical: "center",
                          horizontal: "left",
                        }}
                      >
                        <List sx={{ display: "flex", flexDirection: "row", p: 0 }}>
                          <MenuItem onClick={addField}>
                            <TextFieldsIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
                            Custom Field
                          </MenuItem>
                        </List>
                      </Menu>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "20px",
              padding: "0px 10px",
            }}
          >
            <Button
              variant="contained"
              className="mpl-primary-btn"
              onClick={() => setCurrentMode("grid")}
            >
              Back
            </Button>
            <Button
              variant="contained"
              className="mpl-primary-btn"
              onClick={onSubmit}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default AgreementPrompt;
